import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';
import { useParams, useNavigate } from 'react-router-dom';
import { TrophyIcon, ArrowLeftIcon, AwardIcon, ClockIcon, PrinterIcon, Loader2 } from 'lucide-react';
import DailyExercisesHero from '../../components/student/dailyExercises/DailyExercisesHero';
import learningLevelsData from '../../data/learningLevels.json';
import api from '../../services/api';
import toast from 'react-hot-toast';

const Leaderboard = () => {
  const { t } = useTranslation();
  const { isRTL, currentLanguage } = useLanguage();
  const { competitionId } = useParams();
  const navigate = useNavigate();

  const [competition, setCompetition] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);

  const handlePrint = () => {
    window.print();
  };

  const fetchLeaderboard = useCallback(async () => {
    setLoading(true);
    try {
      const competitionResponse = await api.get(`/competitions/${competitionId}`);
      if (competitionResponse.data.success) {
        setCompetition(competitionResponse.data.data);
      }

      const leaderboardResponse = await api.get(`/competitions/${competitionId}/leaderboard?page=1&limit=500`);
      if (leaderboardResponse.data.success) {
        setLeaderboard(leaderboardResponse.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
      toast.error(t('competitions.fetchError'));
    } finally {
      setLoading(false);
    }
  }, [competitionId, t]);

  useEffect(() => {
    fetchLeaderboard();
  }, [fetchLeaderboard]);

  /** ترجمة اسم المستوى (المفتاح عادة الاسم العربي القادم من الـ API) */
  const translateLevelName = (levelName) => {
    if (!levelName) return '';
    const translations = learningLevelsData[levelName] || {};
    return translations[currentLanguage] || levelName;
  };

  const getMedalColor = (rank) => {
    if (rank === 1) return 'text-amber-500';
    if (rank === 2) return 'text-slate-400';
    if (rank === 3) return 'text-emerald-700';
    return 'text-slate-600';
  };

  const getRowBackground = (rank) => {
    if (rank === 1) return 'bg-amber-50/80';
    if (rank === 2) return 'bg-slate-50/90';
    if (rank === 3) return 'bg-emerald-50/70';
    return 'bg-white';
  };

  const getStudentName = (participant) => {
    return `${participant.firstName || ''} ${participant.lastName || ''}`.trim() || participant.username;
  };

  const formatTime = (milliseconds) => {
    if (!milliseconds) return '00:00:00';
    const totalMs = parseInt(milliseconds, 10);
    const minutes = Math.floor(totalMs / 60000);
    const seconds = Math.floor((totalMs % 60000) / 1000);
    const centiseconds = Math.floor((totalMs % 1000) / 10);
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}:${String(centiseconds).padStart(2, '0')}`;
  };

  const totalRounds = competition?.totalRounds;

  if (loading) {
    return (
      <div
        className="min-h-screen bg-gradient-to-b from-sky-100/90 via-violet-50/40 to-amber-50/50"
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        <div className="mx-auto max-w-6xl px-3 py-10 sm:px-4">
          <div className="rounded-[1.35rem] border-[3px] border-sky-200/80 bg-gradient-to-br from-white to-sky-50/80 px-6 py-14 text-center shadow-[0_8px_0_0_rgba(14,165,233,0.1)]">
            <Loader2 className="mx-auto h-12 w-12 animate-spin text-sky-600" aria-hidden strokeWidth={2.25} />
            <p className="mt-4 text-sm font-bold text-slate-600 sm:text-base">{t('common.loading')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!competition && leaderboard.length === 0) {
    return (
      <div
        className="min-h-screen bg-gradient-to-b from-sky-100/90 via-violet-50/40 to-amber-50/50"
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        <div className="mx-auto max-w-6xl px-3 py-10 sm:px-4">
          <div className="rounded-[1.35rem] border-[3px] border-slate-200/90 bg-gradient-to-br from-white to-sky-50/60 px-6 py-14 text-center shadow-[0_8px_0_0_rgba(14,165,233,0.08)]">
            <p className="text-lg font-black text-slate-900">{t('competitions.notFound')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        @media print {
          body {
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
          .print\\:hidden {
            display: none !important;
          }
          table {
            page-break-inside: auto;
          }
          tr {
            page-break-inside: avoid;
            page-break-after: auto;
          }
          thead {
            display: table-header-group;
          }
          .shadow-lg, .shadow-md, .shadow-xl, .shadow-2xl, .shadow-\\[0_8px_0_0_rgba\\(15\\,23\\,42\\,0\\.06\\)\\] {
            box-shadow: none !important;
          }
          @page {
            margin: 1cm;
          }
        }
      `}</style>
      <div
        className="min-h-screen bg-gradient-to-b from-sky-100/90 via-violet-50/40 to-amber-50/50"
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        <div className="print:hidden border-b-[3px] border-slate-900/10 bg-white/90 shadow-sm">
          <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-3 py-3 sm:px-4">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className={`inline-flex min-h-[2.5rem] items-center gap-2 rounded-2xl border-[3px] border-slate-900/10 bg-white px-4 text-sm font-bold text-slate-800 shadow-[0_4px_0_0_rgba(15,23,42,0.08)] transition hover:bg-sky-50 ${isRTL ? 'flex-row-reverse' : ''}`}
            >
              <ArrowLeftIcon className={`h-4 w-4 shrink-0 ${isRTL ? 'rotate-180' : ''}`} aria-hidden />
              {t('common.back')}
            </button>
            <button
              type="button"
              onClick={handlePrint}
              className={`inline-flex min-h-[2.5rem] items-center gap-2 rounded-2xl border-[3px] border-sky-200 bg-sky-50 px-4 text-sm font-bold text-sky-900 shadow-sm transition hover:bg-sky-100 ${isRTL ? 'flex-row-reverse' : ''}`}
            >
              <PrinterIcon className="h-4 w-4 shrink-0" aria-hidden />
              {t('common.print')}
            </button>
          </div>
        </div>

        <div className="relative mx-auto w-full max-w-6xl space-y-5 px-3 pb-8 sm:space-y-6 sm:px-4 sm:pb-10">
          <DailyExercisesHero
            title={competition?.name || t('navigation.leaderboard')}
            description={t('competitions.leaderboardPageDescription')}
            icon={TrophyIcon}
            iconClassName="text-amber-600"
          />

          {leaderboard.length >= 3 && (
            <div className="grid grid-cols-1 gap-4 py-4 md:grid-cols-3 md:gap-5 md:py-6">
              <div className="order-2 md:order-1">
                <div className="rounded-[1.35rem] border-[3px] border-slate-200/90 bg-white px-5 py-8 text-center shadow-[0_8px_0_0_rgba(15,23,42,0.06)] transition hover:shadow-md sm:py-9">
                  <div className="mb-3 flex justify-center">
                    <div className="flex h-16 w-16 items-center justify-center rounded-2xl border-[3px] border-slate-200 bg-slate-100">
                      <AwardIcon className="h-9 w-9 text-slate-400" strokeWidth={2.25} />
                    </div>
                  </div>
                  <div className="mb-1 text-3xl font-black text-slate-400">2</div>
                  <h3 className="mb-1 line-clamp-2 text-lg font-black text-slate-900">{getStudentName(leaderboard[1])}</h3>
                  <p className="mb-2 text-xs font-semibold text-slate-500">
                    {translateLevelName(leaderboard[1].levelName) || '—'}
                  </p>
                  <p className="text-2xl font-black tabular-nums text-violet-700">{leaderboard[1].totalScore}</p>
                  <p className="mt-1 text-xs font-bold uppercase tracking-wide text-slate-500">{t('competitions.score')}</p>
                </div>
              </div>

              <div className="order-1 md:order-2">
                <div className="rounded-[1.35rem] border-[3px] border-amber-300/90 bg-gradient-to-br from-amber-400 to-amber-600 px-6 py-9 text-center shadow-[0_8px_0_0_rgba(180,83,9,0.2)] md:-mt-1 md:scale-[1.02] sm:py-10">
                  <div className="mb-3 flex justify-center">
                    <div className="flex h-20 w-20 items-center justify-center rounded-2xl border-[3px] border-white/90 bg-white shadow-md">
                      <TrophyIcon className="h-11 w-11 text-amber-600" strokeWidth={2.25} />
                    </div>
                  </div>
                  <div className="mb-1 text-4xl font-black text-white">1</div>
                  <h3 className="mb-1 line-clamp-2 text-xl font-black text-white">{getStudentName(leaderboard[0])}</h3>
                  <p className="mb-2 text-xs font-semibold text-amber-100">
                    {translateLevelName(leaderboard[0].levelName) || '—'}
                  </p>
                  <p className="text-3xl font-black tabular-nums text-white">{leaderboard[0].totalScore}</p>
                  <p className="mt-1 text-xs font-bold uppercase tracking-wide text-amber-100">{t('competitions.score')}</p>
                </div>
              </div>

              <div className="order-3">
                <div className="rounded-[1.35rem] border-[3px] border-emerald-200/90 bg-white px-5 py-8 text-center shadow-[0_8px_0_0_rgba(15,23,42,0.06)] transition hover:shadow-md sm:py-9">
                  <div className="mb-3 flex justify-center">
                    <div className="flex h-16 w-16 items-center justify-center rounded-2xl border-[3px] border-emerald-200 bg-emerald-50">
                      <AwardIcon className="h-9 w-9 text-emerald-700" strokeWidth={2.25} />
                    </div>
                  </div>
                  <div className="mb-1 text-3xl font-black text-emerald-700">3</div>
                  <h3 className="mb-1 line-clamp-2 text-lg font-black text-slate-900">{getStudentName(leaderboard[2])}</h3>
                  <p className="mb-2 text-xs font-semibold text-slate-500">
                    {translateLevelName(leaderboard[2].levelName) || '—'}
                  </p>
                  <p className="text-2xl font-black tabular-nums text-violet-700">{leaderboard[2].totalScore}</p>
                  <p className="mt-1 text-xs font-bold uppercase tracking-wide text-slate-500">{t('competitions.score')}</p>
                </div>
              </div>
            </div>
          )}

          <section className="overflow-hidden rounded-[1.35rem] border-[3px] border-slate-900/10 bg-white/95 shadow-[0_8px_0_0_rgba(15,23,42,0.06)]">
            <div className="border-b border-slate-200/90 bg-gradient-to-r from-amber-50/90 via-white to-violet-50/50 px-4 py-3 sm:px-5">
              <h2 className="text-base font-black tracking-tight text-slate-900 sm:text-lg">
                {t('competitions.competitionRanking')}
              </h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px] text-sm">
                <thead>
                  <tr className="border-b-2 border-slate-200/90 bg-slate-50/80">
                    <th
                      className={`px-3 py-3.5 text-[11px] font-extrabold uppercase tracking-wide text-slate-500 ${isRTL ? 'text-right' : 'text-left'}`}
                    >
                      {t('competitions.rank')}
                    </th>
                    <th
                      className={`px-3 py-3.5 text-[11px] font-extrabold uppercase tracking-wide text-slate-500 ${isRTL ? 'text-right' : 'text-left'}`}
                    >
                      {t('common.name')}
                    </th>
                    <th className="whitespace-nowrap px-3 py-3.5 text-center text-[11px] font-extrabold uppercase tracking-wide text-slate-500">
                      {t('competitions.rounds')}
                    </th>
                    <th className="whitespace-nowrap px-3 py-3.5 text-center text-[11px] font-extrabold uppercase tracking-wide text-slate-500">
                      {t('competitions.totalScore')}
                    </th>
                    <th className="px-3 py-3.5 text-center text-[11px] font-extrabold uppercase tracking-wide text-slate-500">
                      <div className="mx-auto max-w-[9rem] overflow-hidden whitespace-nowrap text-ellipsis [direction:rtl] text-left">
                        <span className="inline-block [direction:ltr]">{t('competitions.totalTime')}</span>
                      </div>
                    </th>
                    <th
                      className={`px-3 py-3.5 text-[11px] font-extrabold uppercase tracking-wide text-slate-500 ${isRTL ? 'text-right' : 'text-left'}`}
                    >
                      {t('competitions.level')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {leaderboard.length > 0 ? (
                    leaderboard.map((participant, index) => (
                      <tr
                        key={participant.studentNumber || participant.username || index}
                        className={`border-b border-slate-100 transition-colors hover:bg-sky-50/50 ${getRowBackground(participant.rankPosition)}`}
                      >
                        <td className={`px-3 py-3.5 whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                          <div
                            className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse justify-end' : 'justify-start'}`}
                          >
                            {participant.rankPosition <= 3 ? (
                              <>
                                <AwardIcon className={`h-6 w-6 shrink-0 ${getMedalColor(participant.rankPosition)}`} />
                                <span
                                  className="text-lg font-black tabular-nums"
                                  style={{
                                    color:
                                      participant.rankPosition === 1
                                        ? '#d97706'
                                        : participant.rankPosition === 2
                                          ? '#64748b'
                                          : '#15803d',
                                  }}
                                >
                                  {participant.rankPosition}
                                </span>
                              </>
                            ) : (
                              <span className="text-lg font-bold text-slate-600">{participant.rankPosition}</span>
                            )}
                          </div>
                        </td>
                        <td className={`min-w-0 max-w-[12rem] px-3 py-3.5 sm:max-w-none ${isRTL ? 'text-right' : 'text-left'}`}>
                          <div className="break-words font-semibold text-slate-900">{getStudentName(participant)}</div>
                          <div className="truncate text-xs text-slate-500">{participant.studentNumber}</div>
                        </td>
                        <td className="whitespace-nowrap px-3 py-3.5 text-center tabular-nums text-slate-800">
                          {participant.completedRounds} / {totalRounds ?? '—'}
                        </td>
                        <td className="px-3 py-3.5 text-center">
                          <span className="text-lg font-black tabular-nums text-violet-700">{participant.totalScore}</span>
                        </td>
                        <td className={`px-3 py-3.5 whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                          <div className={`inline-flex items-center gap-1.5 ${isRTL ? 'flex-row-reverse' : ''}`}>
                            <ClockIcon className="h-4 w-4 shrink-0 text-sky-500" aria-hidden />
                            <span className="font-semibold tabular-nums text-slate-800">{formatTime(participant.totalTimeMs)}</span>
                          </div>
                        </td>
                        <td className={`px-3 py-3.5 ${isRTL ? 'text-right' : 'text-left'}`}>
                          <span className="text-sm font-semibold text-slate-800">
                            {translateLevelName(participant.levelName) || '—'}
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="px-6 py-14 text-center">
                        <div className="mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-2xl border-[3px] border-slate-200 bg-slate-50">
                          <TrophyIcon className="h-8 w-8 text-slate-300" aria-hidden />
                        </div>
                        <p className="font-bold text-slate-600">{t('competitions.noStudents')}</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default Leaderboard;
